import { useEffect, useMemo, useRef } from 'react'

type Props = {
  bottomPx?: number
  isEnabled?: boolean
  throttleMs?: number
  onIntersect: () => void
}

export default function IntersectionAnchor({
  bottomPx = 600,
  isEnabled = true,
  throttleMs = 0,
  onIntersect,
}: Props) {
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const lastIntersectAtMsRef = useRef<number>(0)

  const rootMargin = useMemo(
    () => `0px 0px ${Math.max(0, bottomPx)}px 0px`,
    [bottomPx],
  )

  useEffect(() => {
    if (!isEnabled) return
    if (!anchorRef.current) return

    const observer = new IntersectionObserver(
      entries => {
        const firstEntry = entries[0]
        if (!firstEntry?.isIntersecting) return

        const nowMs = Date.now()
        if (throttleMs > 0 && nowMs - lastIntersectAtMsRef.current < throttleMs)
          return
        lastIntersectAtMsRef.current = nowMs

        onIntersect()
      },
      { root: null, rootMargin, threshold: 0 },
    )

    observer.observe(anchorRef.current)

    return () => observer.disconnect()
  }, [isEnabled, onIntersect, rootMargin, throttleMs])

  return <div ref={anchorRef} className='h-px w-full' />
}
